import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { dataCollectionParser } from '../lib/response/dataParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { GrowthCampaign } from '../lib/interfaces/growthCampaign';
import _ from 'lodash';

interface GrowthCampaignStoreState {
  growthCampaigns: GrowthCampaign[];
}

export const state = (): GrowthCampaignStoreState => ({
  growthCampaigns: []
});

export const getters = {
  keywordGrowthCampaigns(state: GrowthCampaignStoreState): GrowthCampaign[] {
    return state.growthCampaigns.filter(x => x.type === 'keyword');
  }
};

export const mutations = {
  setGrowthCampaigns(
    state: GrowthCampaignStoreState,
    growthCampaigns: GrowthCampaign[]
  ): void {
    state.growthCampaigns = growthCampaigns;
  },

  replaceGrowthCampaign(
    state: GrowthCampaignStoreState,
    growthCampaign: GrowthCampaign
  ): void {
    const growthCampaignIndex = state.growthCampaigns.findIndex(
      x => x.id === growthCampaign.id
    );
    if (growthCampaignIndex !== -1) {
      state.growthCampaigns.splice(growthCampaignIndex, 1, growthCampaign);
    }
  },

  addGrowthCampaign(
    state: GrowthCampaignStoreState,
    growthCampaign: GrowthCampaign
  ): void {
    state.growthCampaigns.push(growthCampaign);
  },

  removeGrowthCampaign(state: GrowthCampaignStoreState, id: string): void {
    state.growthCampaigns = state.growthCampaigns.filter(x => x.id !== id);
  }
};

export const actions = {
  async loadGrowthCampaigns({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/growth-campaigns-index'
      );

      commit('setGrowthCampaigns', dataCollectionParser(response));

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadGrowthCampaign(
    { commit },
    { id }: Pick<GrowthCampaign, 'id'>
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/growth-campaigns-show',
        {
          params: {
            id
          }
        }
      );

      const growthCampaign = {
        id: response.data.data.id,
        ...response.data.data.attributes
      };

      commit('replaceGrowthCampaign', growthCampaign);

      return { success: true, errors: {}, body: growthCampaign };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createGrowthCampaign(
    { commit },
    payload: Pick<
      GrowthCampaign,
      | 'keyword'
      | 'welcomeMessage'
      | 'alwaysSendWelcomeMessage'
      | 'listIds'
      | 'customerCarePhone'
      | 'enabled'
    >
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/growth-campaigns-create',
        {
          data: {
            type: 'growthCampaigns',
            attributes: payload
          }
        }
      );

      const growthCampaign = {
        id: response.data.data.id,
        ...response.data.data.attributes
      };

      commit('addGrowthCampaign', growthCampaign);

      return { success: true, errors: {}, body: growthCampaign };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateGrowthCampaign(
    { commit },
    { id, ...attributes }: { id: string } & Partial<GrowthCampaign>
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch(
        '/backend/growth-campaigns-update',
        {
          data: {
            type: 'growthCampaigns',
            id,
            attributes
          }
        },
        {
          params: {
            id
          }
        }
      );

      const growthCampaign = {
        id: response.data.data.id,
        ...response.data.data.attributes
      };

      commit('replaceGrowthCampaign', growthCampaign);

      return { success: true, errors: {}, body: growthCampaign };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async deleteGrowthCampaign(
    { commit },
    { id }: Pick<GrowthCampaign, 'id'>
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().delete(
        '/backend/growth-campaigns-delete',
        {
          params: {
            id
          }
        }
      );

      commit('removeGrowthCampaign', id);

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadGrowthCampaignSmsAction(
    { commit },
    { clientId, growthCampaignId }: Record<string, string>
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().get(
        '/backend/growth-campaign-sms-actions-show',
        {
          params: {
            clientId,
            growthCampaignId
          },
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            return data;
          }
        }
      );

      const growthCampaignSmsAction = {
        id: response.data.data.id,
        ...response.data.data.attributes
      };

      return { success: true, errors: {}, body: growthCampaignSmsAction };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
